import { useState, useEffect } from 'react';
import { CreditCard, Users, Plus, Edit2, Info, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { 
  BillingCycle, 
  ProductBillingCycles,
  CreditManagementSettings as CreditSettings,
  SubscriptionPlan,
  CreditPackage,
  OneTimePaymentPlan,
  DEFAULT_BILLING_CYCLES 
} from '@/types/billing';
import { BillingCyclesConfiguration } from '@/components/billing/BillingCyclesConfiguration';
import { CreditManagementSettings } from '@/components/billing/CreditManagementSettings';
import { CurrencyConfiguration } from '@/components/billing/CurrencyConfiguration';
import { SubscriptionPlansList } from '@/components/billing/plans/SubscriptionPlansList';
import { SubscriptionPlanEditor } from '@/components/billing/plans/SubscriptionPlanEditor';
import { CreditPackagesList } from '@/components/billing/packages/CreditPackagesList';
import { CreditPackageEditor } from '@/components/billing/packages/CreditPackageEditor';
import { OneTimePlansList } from '@/components/billing/one-time-plans/OneTimePlansList';
import { OneTimePlanEditor } from '@/components/billing/one-time-plans/OneTimePlanEditor';
import { useOneTimePlans } from '@/hooks/billing/useOneTimePlans';

interface ProductBillingTabProps {
  productId: string;
  supportedLanguages?: string[];
}


export default function ProductBillingTab({ productId, supportedLanguages = ['en'] }: ProductBillingTabProps) {
  const { t, i18n } = useTranslation();
  const resolvedLanguage = i18n.language ?? i18n.resolvedLanguage ?? 'en';
  const baseLanguage = resolvedLanguage.split('-')[0] ?? resolvedLanguage;
  const activeLanguage = supportedLanguages.includes(resolvedLanguage)
    ? resolvedLanguage
    : supportedLanguages.includes(baseLanguage)
      ? baseLanguage
      : supportedLanguages[0] ?? 'en';
  const [showSubscriptionPlanEditor, setShowSubscriptionPlanEditor] = useState(false);
  const [editingSubscriptionPlan, setEditingSubscriptionPlan] = useState<Partial<SubscriptionPlan> | undefined>();
  const [showCreditPackageEditor, setShowCreditPackageEditor] = useState(false);
  const [editingCreditPackage, setEditingCreditPackage] = useState<Partial<CreditPackage> | undefined>();
  const [creditPackagePlanType, setCreditPackagePlanType] = useState<'individual' | 'teams'>('individual');
  const [showOneTimePlanEditor, setShowOneTimePlanEditor] = useState(false);
  const [editingOneTimePlan, setEditingOneTimePlan] = useState<Partial<OneTimePaymentPlan> | undefined>();
  const [oneTimePlanType, setOneTimePlanType] = useState<'individual' | 'teams'>('individual');
  const [billingConfig, setBillingConfig] = useState({
    individualPlans: {
      enabled: true,
      subscriptionEnabled: true,
      usageBasedEnabled: true,
      oneTimeEnabled: false
    },
    teamsPlans: {
      enabled: true,
      subscriptionEnabled: true,
      usageBasedEnabled: true,
      oneTimeEnabled: false
    }
  });

  const [availableCycles, setAvailableCycles] = useState<BillingCycle[]>([
    { ...DEFAULT_BILLING_CYCLES[0], isDefault: true }, // Monthly
    { ...DEFAULT_BILLING_CYCLES[3], isDefault: true }, // Annual
  ]);

  // Fetch one-time plans to auto-enable toggles (using same params as OneTimePlansList)
  const { data: oneTimePlans = [] } = useOneTimePlans(
    productId,
    {},
    { field: 'name' }
  );


  // Auto-enable one-time payment toggles when one-time plans exist
  useEffect(() => {
    if (oneTimePlans.length > 0) {
      // Check which plan types have one-time plans
      const hasIndividualPlans = oneTimePlans.some(plan => plan.type === 'individual');
      const hasTeamsPlans = oneTimePlans.some(plan => plan.type === 'teams');

      setBillingConfig(prevConfig => ({
        ...prevConfig,
        individualPlans: {
          ...prevConfig.individualPlans,
          oneTimeEnabled: prevConfig.individualPlans.oneTimeEnabled || hasIndividualPlans
        },
        teamsPlans: {
          ...prevConfig.teamsPlans,
          oneTimeEnabled: prevConfig.teamsPlans.oneTimeEnabled || hasTeamsPlans
        }
      }));
    }
  }, [oneTimePlans]);

  
  // Currency state - initialize with USD as fallback until API data loads
  const [activeCurrencies, setActiveCurrencies] = useState<string[]>([]);
  const [defaultCurrency, setDefaultCurrency] = useState<string>('USD');
  
  
  const [showPreview, setShowPreview] = useState(false);

  const shortestCycle = availableCycles.length > 0 
    ? availableCycles.reduce((shortest, current) => 
        current.months < shortest.months ? current : shortest
      )
    : null;


  const handleSaveBillingCycles = (config: ProductBillingCycles) => {
    setAvailableCycles(config.availableCycles);
  };

  const handleSaveCreditSettings = (settings: CreditSettings) => {
    // Handle credit settings save - will be implemented with API integration
    console.log('Credit settings saved:', settings);
  };


  return (
    <div className="space-y-6">
      {/* Stripe Configuration */}
      <Card>
        <CardHeader>
          <CardTitle>{t('products.stripeIntegration')}</CardTitle>
          <CardDescription>{t('products.paymentIntegrationDescription')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <p className="font-medium">{t('products.stripeLabel')}</p>
                <p className="text-sm text-muted-foreground">{t('products.notConnected')}</p>
              </div>
              <Button>
                <CreditCard className="h-4 w-4 mr-2" />
                {t('products.connectStripe')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Currency Configuration */}
      <CurrencyConfiguration 
        productId={productId}
        activeCurrencies={activeCurrencies}
        defaultCurrency={defaultCurrency}
        onCurrenciesChange={setActiveCurrencies}
        onDefaultChange={setDefaultCurrency}
      />

      {/* Billing Cycles Configuration */}
      <BillingCyclesConfiguration 
        productId={productId}
        onSave={handleSaveBillingCycles}
      />

      <Separator className="my-8" />

      {/* Billing Configuration */}
      <Card>
        <CardHeader>
          <CardTitle>{t('products.billingConfiguration')}</CardTitle>
          <CardDescription>
            {t('products.billingConfigurationDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              {t('products.combineBillingModelsDescription')}
            </AlertDescription>
          </Alert>

          {/* Plan Types */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">{t('products.planTypes')}</h3>
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Switch
                  id="individual-plans"
                  checked={billingConfig.individualPlans.enabled}
                  onCheckedChange={(checked) => setBillingConfig({ 
                    ...billingConfig, 
                    individualPlans: { ...billingConfig.individualPlans, enabled: checked }
                  })}
                />
                <Label htmlFor="individual-plans" className="cursor-pointer">
                  {t('products.individualPlans')} ({t('products.individualPlansDescription')})
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="teams-plans"
                  checked={billingConfig.teamsPlans.enabled}
                  onCheckedChange={(checked) => setBillingConfig({ 
                    ...billingConfig, 
                    teamsPlans: { ...billingConfig.teamsPlans, enabled: checked }
                  })}
                />
                <Label htmlFor="teams-plans" className="cursor-pointer">
                  {t('products.teamsPlans')} ({t('products.teamsPlansDescription')})
                </Label>
              </div>
            </div>
          </div>

          {/* Individual Plans Section */}
          {billingConfig.individualPlans.enabled && (
            <Accordion type="single" collapsible defaultValue="individual" className="w-full">
              <AccordionItem value="individual">
                <AccordionTrigger>
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    {t('products.individualPlansConfiguration')}
                  </div>
                </AccordionTrigger>
                <AccordionContent className="space-y-6 pt-4">
                  {/* Subscription Plans */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Switch
                          id="subscription-individual"
                          checked={billingConfig.individualPlans.subscriptionEnabled}
                          onCheckedChange={(checked) => setBillingConfig({ 
                            ...billingConfig, 
                            individualPlans: { ...billingConfig.individualPlans, subscriptionEnabled: checked }
                          })}
                        />
                        <Label htmlFor="subscription-individual" className="text-base font-medium">
                          {t('products.subscriptionBasedPricing')}
                        </Label>
                      </div>
                      {billingConfig.individualPlans.subscriptionEnabled && (
                        <Button 
                          size="sm" 
                          onClick={() => {
                            setEditingSubscriptionPlan({ type: 'individual' });
                            setShowSubscriptionPlanEditor(true);
                          }}
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          {t('products.addPlan')}
                        </Button>
                      )}
                    </div>
                    
                    {billingConfig.individualPlans.subscriptionEnabled && (
                      <div className="ml-6">
                        <SubscriptionPlansList
                          productId={productId}
                          planType="individual"
                          onEditPlan={(plan) => {
                            setEditingSubscriptionPlan(plan);
                            setShowSubscriptionPlanEditor(true);
                          }}
                          defaultCurrency={defaultCurrency}
                          activeCurrencies={activeCurrencies}
                          currentLanguage={activeLanguage}
                        />
                      </div>
                    )}
                  </div>

                  {/* Usage-based Pricing */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Switch
                          id="usage-individual"
                          checked={billingConfig.individualPlans.usageBasedEnabled}
                          onCheckedChange={(checked) => setBillingConfig({ 
                            ...billingConfig, 
                            individualPlans: { ...billingConfig.individualPlans, usageBasedEnabled: checked }
                          })}
                        />
                        <Label htmlFor="usage-individual" className="text-base font-medium">
                          {t('products.usageBased')} ({t('products.credits')})
                        </Label>
                      </div>
                      {billingConfig.individualPlans.usageBasedEnabled && (
                        <Button 
                          size="sm"
                          onClick={() => {
                            setEditingCreditPackage(undefined);
                            setCreditPackagePlanType('individual');
                            setShowCreditPackageEditor(true);
                          }}
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          {t('products.addPackage')}
                        </Button>
                      )}
                    </div>
                    
                    {billingConfig.individualPlans.usageBasedEnabled && (
                      <div className="ml-6" id="credit-packages-list">
                        <CreditPackagesList
                          productId={productId}
                          onEditPackage={(pkg) => {
                            setEditingCreditPackage(pkg);
                            setCreditPackagePlanType('individual');
                            setShowCreditPackageEditor(true);
                          }}
                          defaultCurrency={defaultCurrency}
                          planType="individual"
                        />
                      </div>
                    )}
                  </div>

                  {/* One-time Payment */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Switch
                          id="onetime-individual"
                          checked={billingConfig.individualPlans.oneTimeEnabled}
                          onCheckedChange={(checked) => setBillingConfig({ 
                            ...billingConfig, 
                            individualPlans: { ...billingConfig.individualPlans, oneTimeEnabled: checked }
                          })}
                        />
                        <Label htmlFor="onetime-individual" className="text-base font-medium">
                          {t('products.oneTimePayment')}
                        </Label>
                      </div>
                      {billingConfig.individualPlans.oneTimeEnabled && (
                        <Button 
                          size="sm"
                          onClick={() => {
                            // For individual plans, check if there's already an existing plan
                            const existingIndividualPlan = oneTimePlans.find(plan => 
                              plan.planType === 'individual' || plan.type === 'individual'
                            );
                            
                            if (existingIndividualPlan) {
                              // Edit the existing plan instead of creating a new one
                              setEditingOneTimePlan(existingIndividualPlan);
                            } else {
                              // No existing plan, create new one
                              setEditingOneTimePlan(undefined);
                            }
                            setOneTimePlanType('individual');
                            setShowOneTimePlanEditor(true);
                          }}
                        >
                          {(() => {
                            const existingIndividualPlan = oneTimePlans.find((plan) =>
                              plan.planType === 'individual' || plan.type === 'individual'
                            );
                            return existingIndividualPlan ? (
                              <>
                                <Edit2 className="h-4 w-4 mr-1" />
                                {t('products.editPlan')}
                              </>
                            ) : (
                              <>
                                <Plus className="h-4 w-4 mr-1" />
                                {t('products.configurePlan')}
                              </>
                            );
                          })()}
                        </Button>
                      )}
                    </div>
                    
                    {billingConfig.individualPlans.oneTimeEnabled && (
                      <>
                        <div className="ml-6 text-sm text-muted-foreground">
                          <Info className="h-4 w-4 inline mr-1" />
                          {t('products.oneTimeIndividualNotice')}
                        </div>
                      </>
                    )}
                    
                    {billingConfig.individualPlans.oneTimeEnabled && (
                      <div className="space-y-3 ml-6">
                        <OneTimePlansList
                          productId={productId}
                          onEditPlan={(plan) => {
                            console.log('ProductBillingTab - Individual section editing plan:', plan);
                            console.log('ProductBillingTab - Plan type:', plan.type);
                            setEditingOneTimePlan(plan);
                            // Preserve the plan's actual type when editing
                            const planType = plan.type || 'individual';
                            console.log('ProductBillingTab - Setting oneTimePlanType to:', planType);
                            setOneTimePlanType(planType);
                            setShowOneTimePlanEditor(true);
                          }}
                          defaultCurrency={defaultCurrency}
                          planType="individual"
                        />
                      </div>
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          )}

          {/* Teams Plans Section */}
          {billingConfig.teamsPlans.enabled && (
            <Accordion type="single" collapsible className="w-full" defaultValue="teams">
              <AccordionItem value="teams">
                <AccordionTrigger>
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    {t('products.teamsPlansConfiguration')}
                  </div>
                </AccordionTrigger>
                <AccordionContent className="space-y-6 pt-4">
                  {/* Teams Subscription Plans */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Switch
                          id="subscription-teams"
                          checked={billingConfig.teamsPlans.subscriptionEnabled}
                          onCheckedChange={(checked) => setBillingConfig({ 
                            ...billingConfig, 
                            teamsPlans: { ...billingConfig.teamsPlans, subscriptionEnabled: checked }
                          })}
                        />
                        <Label htmlFor="subscription-teams" className="text-base font-medium">
                          {t('products.subscriptionBased')}
                        </Label>
                      </div>
                      {billingConfig.teamsPlans.subscriptionEnabled && (
                        <Button 
                          size="sm" 
                          onClick={() => {
                            setEditingSubscriptionPlan({ type: 'teams' });
                            setShowSubscriptionPlanEditor(true);
                          }}
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          {t('products.addPlan')}
                        </Button>
                      )}
                    </div>
                    
                    {billingConfig.teamsPlans.subscriptionEnabled && (
                      <div className="ml-6">
                        <SubscriptionPlansList
                          productId={productId}
                          planType="teams"
                          onEditPlan={(plan) => {
                            setEditingSubscriptionPlan(plan);
                            setShowSubscriptionPlanEditor(true);
                          }}
                          defaultCurrency={defaultCurrency}
                          activeCurrencies={activeCurrencies}
                          currentLanguage={activeLanguage}
                        />
                      </div>
                    )}
                  </div>

                  {/* Teams Usage-based Pricing */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Switch
                          id="usage-teams"
                          checked={billingConfig.teamsPlans.usageBasedEnabled}
                          onCheckedChange={(checked) => setBillingConfig({ 
                            ...billingConfig, 
                            teamsPlans: { ...billingConfig.teamsPlans, usageBasedEnabled: checked }
                          })}
                        />
                        <Label htmlFor="usage-teams" className="text-base font-medium">
                          {t('products.usageBased')} ({t('products.credits')})
                        </Label>
                      </div>
                      {billingConfig.teamsPlans.usageBasedEnabled && (
                        <Button 
                          size="sm"
                          onClick={() => {
                            setEditingCreditPackage(undefined);
                            setCreditPackagePlanType('teams');
                            setShowCreditPackageEditor(true);
                          }}
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          {t('products.addPackage')}
                        </Button>
                      )}
                    </div>
                    
                    {billingConfig.teamsPlans.usageBasedEnabled && (
                      <div className="space-y-3 ml-6">
                        <Alert>
                          <Info className="h-4 w-4" />
                          <AlertDescription>
                            {t('products.sharedCreditsDescription')}
                          </AlertDescription>
                        </Alert>
                        <CreditPackagesList
                          productId={productId}
                          onEditPackage={(pkg) => {
                            setEditingCreditPackage(pkg);
                            setCreditPackagePlanType('teams');
                            setShowCreditPackageEditor(true);
                          }}
                          defaultCurrency={defaultCurrency}
                          planType="teams"
                        />
                      </div>
                    )}
                  </div>

                  {/* Teams One-time Payment */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Switch
                          id="onetime-teams"
                          checked={billingConfig.teamsPlans.oneTimeEnabled}
                          onCheckedChange={(checked) => setBillingConfig({ 
                            ...billingConfig, 
                            teamsPlans: { ...billingConfig.teamsPlans, oneTimeEnabled: checked }
                          })}
                        />
                        <Label htmlFor="onetime-teams" className="text-base font-medium">
                          {t('products.oneTimePayment')}
                        </Label>
                      </div>
                      {billingConfig.teamsPlans.oneTimeEnabled && (
                        <Button 
                          size="sm"
                          onClick={() => {
                            setEditingOneTimePlan(undefined);
                            setOneTimePlanType('teams');
                            setShowOneTimePlanEditor(true);
                          }}
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          {t('products.configurePlan')}
                        </Button>
                      )}
                    </div>
                    
                    {billingConfig.teamsPlans.oneTimeEnabled && (
                      <div className="space-y-3 ml-6">
                        <OneTimePlansList
                          productId={productId}
                          onEditPlan={(plan) => {
                            console.log('ProductBillingTab - Teams section editing plan:', plan);
                            console.log('ProductBillingTab - Plan type:', plan.type);
                            setEditingOneTimePlan(plan);
                            // Preserve the plan's actual type when editing
                            const planType = plan.type || 'teams';
                            console.log('ProductBillingTab - Setting oneTimePlanType to:', planType);
                            setOneTimePlanType(planType);
                            setShowOneTimePlanEditor(true);
                          }}
                          defaultCurrency={defaultCurrency}
                          planType="teams"
                        />
                      </div>
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          )}
        </CardContent>
      </Card>

      {/* Credit Management Settings - Only show when credits are used */}
      {((billingConfig.individualPlans.enabled && (billingConfig.individualPlans.usageBasedEnabled || billingConfig.individualPlans.subscriptionEnabled)) || 
        (billingConfig.teamsPlans.enabled && (billingConfig.teamsPlans.usageBasedEnabled || billingConfig.teamsPlans.subscriptionEnabled))) && (
        <CreditManagementSettings
          productId={productId}
          shortestCycleName={shortestCycle?.name.toLowerCase() || 'month'}
          onSave={handleSaveCreditSettings}
        />
      )}

      {/* Customer Pricing Preview */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>{t('products.customerPreview')}</CardTitle>
            <CardDescription>{t('products.customerPreviewDescription')}</CardDescription>
          </div>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setShowPreview(!showPreview)}
          >
            <Eye className="h-4 w-4 mr-2" />
            {showPreview ? t('common.hide') : t('common.show')} {t('products.previewLabel')}
          </Button>
        </CardHeader>
        {showPreview && (
          <CardContent>
            <div className="space-y-6 text-sm text-muted-foreground">
              {billingConfig.individualPlans.enabled && (
                <div>
                  <h3 className="text-lg font-semibold mb-2">{t('products.forIndividuals')}</h3>
                  <p>{t('products.previewIndividualPlaceholder')}</p>
                </div>
              )}
              {billingConfig.teamsPlans.enabled && (
                <div>
                  <h3 className="text-lg font-semibold mb-2">{t('products.forTeams')}</h3>
                  <p>{t('products.previewTeamsPlaceholder')}</p>
                </div>
              )}
            </div>
          </CardContent>
        )}
      </Card>


      {/* Subscription Plan Editor */}
      <SubscriptionPlanEditor
        productId={productId}
        plan={editingSubscriptionPlan}
        isOpen={showSubscriptionPlanEditor}
        onClose={() => {
          setShowSubscriptionPlanEditor(false);
          setEditingSubscriptionPlan(undefined);
        }}
        activeCurrencies={activeCurrencies}
        defaultCurrency={defaultCurrency}
        supportedLanguages={supportedLanguages}
        currentLanguage={activeLanguage}
      />

      {/* Credit Package Editor */}
      <CreditPackageEditor
        productId={productId}
        creditPackage={editingCreditPackage}
        isOpen={showCreditPackageEditor}
        onClose={() => {
          setShowCreditPackageEditor(false);
          setEditingCreditPackage(undefined);
        }}
        activeCurrencies={activeCurrencies}
        defaultCurrency={defaultCurrency}
        planType={creditPackagePlanType}
        supportedLanguages={supportedLanguages}
      />

      {/* One-Time Plan Editor */}
      <OneTimePlanEditor
        productId={productId}
        plan={editingOneTimePlan}
        isOpen={showOneTimePlanEditor}
        onClose={() => {
          console.log('ProductBillingTab - OneTimePlanEditor onClose called');
          setShowOneTimePlanEditor(false);
          setEditingOneTimePlan(undefined);
        }}
        activeCurrencies={activeCurrencies}
        defaultCurrency={defaultCurrency}
        supportedLanguages={supportedLanguages}
        planType={oneTimePlanType}
      />
    </div>
  );
}
